/**
 * Local Bridge MarketFit: list deals and create new deals for a bridge session.
 * Uses sessionId from query; company_id and bridge_session_id come from the session.
 * Only shown when Local Bridge API is enabled.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, Button, Table, message, Spin, Alert, Space, Typography, Tabs } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { AimOutlined, ArrowLeftOutlined, PlusOutlined, DollarOutlined, AppstoreOutlined, BulbOutlined } from '@ant-design/icons';
import { isLocalBridgeApiEnabled } from '../services/bridgeSessionDataAdapter';
import {
  getBridgeSession,
  getCompany,
  listMarketFitDeals,
  listMarketFitProducts,
  listMarketFitSituations,
  type LocalBridgeSession,
  type MarketFitDeal,
  type MarketFitProduct,
  type MarketFitSituation,
} from '../services/localBridgeSessionApi';
import { LocalBridgeNewDealDialog } from '../components/LocalBridge/LocalBridgeNewDealDialog';
import { ProductDefinitionDrawer } from '../components/LocalBridge/ProductDefinitionDrawer';
import { SituationDefinitionDrawer } from '../components/LocalBridge/SituationDefinitionDrawer';
import { DealConnectionsDrawer } from '../components/LocalBridge/DealConnectionsDrawer';

const { Text } = Typography;

export const LocalBridgeMarketFitPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('sessionId');

  const [session, setSession] = useState<LocalBridgeSession | null>(null);
  const [deals, setDeals] = useState<MarketFitDeal[]>([]);
  const [products, setProducts] = useState<MarketFitProduct[]>([]);
  const [situations, setSituations] = useState<MarketFitSituation[]>([]);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [newDealOpen, setNewDealOpen] = useState(false);
  const [productDefineId, setProductDefineId] = useState<string | null>(null);
  const [situationDefineId, setSituationDefineId] = useState<string | null>(null);
  const [dealConnectionsId, setDealConnectionsId] = useState<string | null>(null);

  const loadSessionAndDeals = useCallback(async () => {
    if (!sessionId || !isLocalBridgeApiEnabled()) {
      setSession(null);
      setDeals([]);
      setProducts([]);
      setSituations([]);
      setCompanyName(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const s = await getBridgeSession(sessionId);
      setSession(s ?? null);
      if (s) {
        const [list, companyRow, pList, sList] = await Promise.all([
          listMarketFitDeals(s.company_id, sessionId),
          getCompany(s.company_id),
          listMarketFitProducts(s.company_id),
          listMarketFitSituations(s.company_id),
        ]);
        setDeals(list);
        setProducts(pList);
        setSituations(sList);
        setCompanyName(companyRow?.name ?? s.company_id);
      } else {
        setDeals([]);
        setProducts([]);
        setSituations([]);
        setCompanyName(null);
      }
    } catch {
      setSession(null);
      setDeals([]);
      setProducts([]);
      setSituations([]);
      setCompanyName(null);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    loadSessionAndDeals();
  }, [loadSessionAndDeals]);

  const handleDealCreated = useCallback(() => {
    setNewDealOpen(false);
    loadSessionAndDeals();
    message.success('Deal created');
  }, [loadSessionAndDeals]);

  if (!isLocalBridgeApiEnabled()) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="info"
          message="Local Bridge API not configured"
          description="Local Bridge API is not available. Ensure the backend is running."
        />
        <Link to="/local-bridge">
          <Button icon={<ArrowLeftOutlined />} style={{ marginTop: 16 }}>
            Back to Local Bridge
          </Button>
        </Link>
      </div>
    );
  }

  if (!sessionId) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="warning"
          message="No session selected"
          description="Open MarketFit from the Local Bridge page: use the MarketFit action on a session row, or add ?sessionId=... to the URL."
        />
        <Link to="/local-bridge">
          <Button icon={<ArrowLeftOutlined />} style={{ marginTop: 16 }}>
            Back to Local Bridge
          </Button>
        </Link>
      </div>
    );
  }

  if (loading && !session) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!session) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="error" message="Session not found" description={`Session ${sessionId} could not be loaded.`} />
        <Link to="/local-bridge">
          <Button icon={<ArrowLeftOutlined />} style={{ marginTop: 16 }}>
            Back to Local Bridge
          </Button>
        </Link>
      </div>
    );
  }

  const getProductNames = (productIds: string[] | undefined) =>
    productIds?.map((id) => products.find((p) => p.id === id)?.name).filter(Boolean).join(', ') ?? '—';
  const getSituationNames = (situationIds: string[] | undefined) =>
    situationIds?.map((id) => situations.find((s) => s.id === id)?.name).filter(Boolean).join(', ') ?? '—';
  const getDealsForProduct = (productId: string) =>
    deals.filter((d) => d.product_ids?.includes(productId)).map((d) => d.name).join(', ') || '—';
  const getDealsForSituation = (situationId: string) =>
    deals.filter((d) => d.situation_ids?.includes(situationId)).map((d) => d.name).join(', ') || '—';

  const dealColumns: ColumnsType<MarketFitDeal> = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'State', dataIndex: 'state', key: 'state', width: 100 },
    { title: 'Products', key: 'products', render: (_, r) => getProductNames(r.product_ids), ellipsis: true },
    { title: 'Situations', key: 'situations', render: (_, r) => getSituationNames(r.situation_ids), ellipsis: true },
    { title: 'Created', dataIndex: 'created_at', key: 'created_at', width: 100, render: (v: string) => (v ? new Date(v).toLocaleDateString() : '—') },
  ];

  const twoPanelLayout = (left: React.ReactNode, right: React.ReactNode) => (
    <div style={{ display: 'flex', gap: 16, minHeight: 420 }}>
      <div style={{ flex: '0 0 42%', minWidth: 0 }}>{left}</div>
      <div style={{ flex: 1, minWidth: 0, minHeight: 420 }}>{right}</div>
    </div>
  );

  const dealsTab = (
    <>
      {twoPanelLayout(
        <Card title="Deals" size="small">
          {loading ? (
            <Spin />
          ) : deals.length === 0 ? (
            <p style={{ color: '#6b7280' }}>No deals yet. Create a deal and attach products and situations.</p>
          ) : (
            <Table
              rowKey="id"
              columns={dealColumns}
              dataSource={deals}
              pagination={false}
              size="small"
              onRow={(r) => ({
                onClick: () => setDealConnectionsId(r.id),
                style: { cursor: 'pointer', backgroundColor: dealConnectionsId === r.id ? 'var(--ant-color-primary-bg)' : undefined },
              })}
            />
          )}
        </Card>,
        dealConnectionsId ? (
          <DealConnectionsDrawer
            variant="panel"
            open
            dealId={dealConnectionsId}
            onClose={() => setDealConnectionsId(null)}
            onSaved={loadSessionAndDeals}
          />
        ) : (
          <Card size="small" style={{ height: '100%' }}>
            <div style={{ padding: 24, color: '#6b7280', textAlign: 'center' }}>Select a deal to view or edit connections.</div>
          </Card>
        )
      )}
    </>
  );

  const productsTab = (
    <>
      {twoPanelLayout(
        <Card title="Products" size="small">
          {loading ? (
            <Spin />
          ) : products.length === 0 ? (
            <p style={{ color: '#6b7280' }}>No products yet. Create a product when creating a deal.</p>
          ) : (
            <Table
              rowKey="id"
              pagination={false}
              size="small"
              dataSource={products}
              columns={[
                { title: 'Name', dataIndex: 'name', key: 'name' },
                { title: 'In deals', key: 'deals', render: (_, r) => getDealsForProduct(r.id), ellipsis: true },
              ]}
              onRow={(r) => ({
                onClick: () => setProductDefineId(r.id),
                style: { cursor: 'pointer', backgroundColor: productDefineId === r.id ? 'var(--ant-color-primary-bg)' : undefined },
              })}
            />
          )}
        </Card>,
        productDefineId ? (
          <ProductDefinitionDrawer
            variant="panel"
            open
            productId={productDefineId}
            companyId={session.company_id}
            onClose={() => setProductDefineId(null)}
            onSaved={loadSessionAndDeals}
          />
        ) : (
          <Card size="small" style={{ height: '100%' }}>
            <div style={{ padding: 24, color: '#6b7280', textAlign: 'center' }}>Select a product to define inputs and benefits.</div>
          </Card>
        )
      )}
    </>
  );

  const situationsTab = (
    <>
      {twoPanelLayout(
        <Card title="Situations" size="small">
          {loading ? (
            <Spin />
          ) : situations.length === 0 ? (
            <p style={{ color: '#6b7280' }}>No situations yet. Create a situation when creating a deal.</p>
          ) : (
            <Table
              rowKey="id"
              pagination={false}
              size="small"
              dataSource={situations}
              columns={[
                { title: 'Name', dataIndex: 'name', key: 'name' },
                { title: 'In deals', key: 'deals', render: (_, r) => getDealsForSituation(r.id), ellipsis: true },
              ]}
              onRow={(r) => ({
                onClick: () => setSituationDefineId(r.id),
                style: { cursor: 'pointer', backgroundColor: situationDefineId === r.id ? 'var(--ant-color-primary-bg)' : undefined },
              })}
            />
          )}
        </Card>,
        situationDefineId ? (
          <SituationDefinitionDrawer
            variant="panel"
            open
            situationId={situationDefineId}
            companyId={session.company_id}
            onClose={() => setSituationDefineId(null)}
            onSaved={loadSessionAndDeals}
          />
        ) : (
          <Card size="small" style={{ height: '100%' }}>
            <div style={{ padding: 24, color: '#6b7280', textAlign: 'center' }}>Select a situation to define assets, needs, and actors.</div>
          </Card>
        )
      )}
    </>
  );

  return (
    <div style={{ padding: 24, maxWidth: 1200 }}>
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <Link to="/local-bridge">
          <Button icon={<ArrowLeftOutlined />}>Local Bridge</Button>
        </Link>
        <AimOutlined style={{ fontSize: 24 }} />
        <div>
          <Typography.Title level={4} style={{ margin: 0 }}>
            MarketFit — {companyName ?? session.company_id}
          </Typography.Title>
          <Text type="secondary">Session: {sessionId.slice(0, 8)}…</Text>
        </div>
        <div style={{ flex: 1 }} />
        <Link to={`/local-bridge/marketfit/guided?sessionId=${sessionId}`}>
          <Button>Guided session</Button>
        </Link>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setNewDealOpen(true)}>
          New Deal
        </Button>
      </div>

      <Tabs
        defaultActiveKey="deals"
        items={[
          { key: 'deals', label: <span><DollarOutlined /> Deals</span>, children: dealsTab },
          { key: 'products', label: <span><AppstoreOutlined /> Products</span>, children: productsTab },
          { key: 'situations', label: <span><BulbOutlined /> Situations</span>, children: situationsTab },
        ]}
      />

      <LocalBridgeNewDealDialog
        open={newDealOpen}
        companyId={session.company_id}
        bridgeSessionId={sessionId}
        onClose={() => setNewDealOpen(false)}
        onSuccess={handleDealCreated}
      />
    </div>
  );
};

export default LocalBridgeMarketFitPage;
